import { createFileRoute, Link } from "@tanstack/react-router";
import { Search, TrendingUp, FileText, Link2, BarChart3, Globe, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/marketing/search-seo/")({
  component: SearchSeoIndex,
  head: () => ({
    meta: [
      { title: "Search & SEO Services — CoderLity" },
      { name: "description", content: "Win organic traffic that compounds over time with technical SEO, content and link building." },
      { property: "og:title", content: "Search & SEO Services — CoderLity" },
      { property: "og:description", content: "Compounding organic traffic from technical SEO, content strategy and authoritative links." },
      { property: "og:url", content: "/marketing/search-seo" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing/search-seo" }],
  }),
});

type Item = { slug: string; title: string; desc: string; href?: string };

const seo: Item[] = [
  { slug: "technical-seo", title: "Technical SEO", desc: "Explore technical seo" },
  { slug: "on-page-seo", title: "On-page SEO", desc: "Explore on-page seo" },
  { slug: "link-building", title: "Link Building", desc: "Explore link building" },
  { slug: "local-seo", title: "Local SEO", desc: "Explore local seo", href: "/marketing/local-seo" },
];

const content: Item[] = [
  { slug: "keyword-research", title: "Keyword Research", desc: "Explore keyword research" },
  { slug: "blog-articles", title: "Blog & Articles", desc: "Explore blog & articles" },
  { slug: "ai-content", title: "AI Content", desc: "Explore ai content" },
];

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) =>
            it.href ? (
              <Link
                key={it.slug}
                to={it.href}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <h3 className="text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            ) : (
              <Link
                key={it.slug}
                to="/marketing/search-seo/$child"
                params={{ child: it.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <h3 className="text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            )
          )}
        </div>
      </div>
    </section>
  );
}

function SearchSeoIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="SEO" items={seo} />
        <Group title="Content" items={content} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Marketing",
  title: "Search & SEO",
  sub: "Win organic traffic that compounds over time.",
  intro:
    "Ads stop the day you stop paying. SEO keeps delivering for years. We build SEO programs that combine technical excellence, content depth and authoritative backlinks — so your traffic curve goes up and to the right while your competitors burn ad budget. Every engagement starts with a full audit and ends with measurable rank, traffic and revenue gains.",
  features: [
    { icon: Search, title: "Keyword strategy", description: "Intent-mapped keywords with realistic difficulty and traffic forecasts." },
    { icon: FileText, title: "Content engine", description: "Briefs, drafts and publishing for pillar pages and clusters." },
    { icon: Globe, title: "Technical SEO", description: "Core Web Vitals, schema, crawl budget and indexation fixes." },
    { icon: Link2, title: "Link building", description: "Digital PR, guest posts and unlinked-mention reclamation." },
    { icon: TrendingUp, title: "Rank tracking", description: "Daily rank monitoring with competitor benchmarking." },
    { icon: BarChart3, title: "Revenue attribution", description: "GA4 + Search Console pipelines that tie traffic to revenue." },
  ],
  process: [
    { step: "01", title: "Audit", description: "Technical, on-page, content and backlink baseline." },
    { step: "02", title: "Strategy", description: "Keyword map, content calendar and link targets." },
    { step: "03", title: "Execute", description: "Monthly content + link sprints with weekly check-ins." },
    { step: "04", title: "Iterate", description: "Refresh underperformers and double down on winners." },
  ],
  deliverables: [
    "Full SEO audit with prioritized fixes",
    "Keyword strategy and content calendar",
    "Monthly content production (4–10 pieces)",
    "Backlink outreach and acquisitions",
    "Monthly rank, traffic and revenue reports",
    "Quarterly strategy reviews",
  ],
  techStack: ["Ahrefs", "Semrush", "Google Search Console", "GA4", "Screaming Frog", "Surfer SEO", "Clearscope"],
  pricing: [
    { name: "Starter", price: "$2.5k", period: "month", features: ["Technical audit", "4 articles / month", "Basic link outreach", "Monthly reporting"] },
    { name: "Growth", price: "$6k", period: "month", highlighted: true, features: ["Full strategy", "8 articles / month", "Active link building", "Bi-weekly calls"] },
    { name: "Enterprise", price: "$15k", period: "month", features: ["Dedicated SEO team", "20+ articles / month", "Digital PR campaigns", "Custom dashboards"] },
  ],
  faqs: [
    { q: "How long until I see results?", a: "Technical wins land in weeks. Content rankings typically appear in 3–6 months. Compound traffic gains show in months 6–12." },
    { q: "Do you guarantee rankings?", a: "No reputable SEO does — Google's algorithm is owned by Google. We do guarantee process: audits, content, links and reporting on a fixed cadence." },
    { q: "Can you fix a Google penalty?", a: "Yes. We diagnose manual and algorithmic penalties, clean up toxic links and file reconsideration requests." },
    { q: "Will you write the content too?", a: "Yes — our writers are subject-matter specialists. You review and approve before publishing." },
  ],
};
